<template>
  <div>
    <v-row>
      <!-- Columna para valores confirmados -->

      <!-- Columna para estado actual (siempre visible) -->
      <v-col cols="6">
        <div class="extra-info-header">
          <v-icon :color="getStatusHeaderColor()" class="mr-1" size="14">{{ getStatusHeaderIcon() }}</v-icon>
          <span class="extra-info-title">{{ getStatusHeaderTitle() }}</span>
        </div>
        <div class="extra-info-content">
          <div
            v-for="(value, key) in getCurrentValuesToShow()"
            :key="'current-'+key"
            class="info-item"
          >
            <span class="info-key">{{ key }}:</span>
            <span :class="getCurrentValueClass(key)">
              {{ value }}
            </span>
          </div>
        </div>
      </v-col>
      <v-col cols="6">
        <div class="extra-info-header">
          <v-icon class="mr-1" size="14">{{ getExtraInfoIcon() }}</v-icon>
          <span class="extra-info-title">{{ getExtraInfoTitle() }}</span>
        </div>
        <div class="extra-info-content">
          <div
            v-for="(value, key) in getExtraInfoToShow()"
            :key="key"
            class="info-item"
          >
            <span class="info-key">{{ key }}:</span>
            <span :class="getValueClass(key)">
              {{ value }}
            </span>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
  import checklistService from '@/services/checklistService'

  export default {
    name: 'ProcessVariables',
    props: {
      parameter: {
        type: Object,
        required: true
      },
      processInfo: {
        type: Object,
        default: null
      },
      readonly: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      hasValueChanges() {
        if (this.parameter.value !== 'checked' || !this.hasExtraInfo || !this.processInfo) {
          return false
        }

        return this.getValueChanges().length > 0
      },
      hasExtraInfo() {
        return this.parameter.extraInfo && Object.keys(this.parameter.extraInfo).length > 0
      }
    },
    methods: {
      getExtraInfoToShow() {
        if (!this.parameter.extraInfo) return {}

        if (this.parameter.value === 'checked' || this.parameter.value === 'not_required') {
          return this.parameter.extraInfo
        } else {
          const currentValues = {}
          Object.keys(this.parameter.extraInfo).forEach(key => {
            currentValues[key] = this.getProcessValue(key)
          })
          return currentValues
        }
      },
      getExtraInfoTitle() {
        if (this.parameter.value === 'checked' || this.parameter.value === 'not_required') {
          return 'Valores confirmados'
        } else {
          return 'Valores del proceso'
        }
      },
      getExtraInfoIcon() {
        if (this.parameter.value === 'checked' || this.parameter.value === 'not_required') {
          return 'history'
        } else {
          return 'refresh'
        }
      },
      getCurrentValuesToShow() {
        if (!this.parameter.extraInfo) return {}

        if (this.parameter.value === 'pending') {
          // Si está pendiente, mostrar "Pendiente de confirmar" para cada campo
          const pendingValues = {}
          Object.keys(this.parameter.extraInfo).forEach(key => {
            pendingValues[key] = 'Confirmación pendiente'
          })
          return pendingValues
        } else {
          // Si está checked o not_required, mostrar valores actuales del proceso
          const currentValues = {}
          Object.keys(this.parameter.extraInfo).forEach(key => {
            currentValues[key] = this.getProcessValue(key)
          })
          return currentValues
        }
      },
      getStatusHeaderTitle() {
        switch (this.parameter.value) {
          case 'pending':
            return 'Estado actual'
          case 'checked':
            return this.hasValueChanges ? 'Valores del proceso (cambios detectados)' : 'Valores del proceso'
          case 'not_required':
            return 'Estado actual'
          default:
            return 'Estado actual'
        }
      },
      getStatusHeaderIcon() {
        switch (this.parameter.value) {
          case 'pending':
            return 'schedule'
          case 'checked':
            return this.hasValueChanges ? 'warning' : 'check_circle'
          case 'not_required':
            return 'check_circle'
          default:
            return 'info'
        }
      },
      getStatusHeaderColor() {
        switch (this.parameter.value) {
          case 'pending':
            return 'warning'
          case 'checked':
            return this.hasValueChanges ? 'error' : 'success'
          case 'not_required':
            return 'success'
          default:
            return 'info'
        }
      },
      getCurrentValueClass(key) {
        switch (this.parameter.value) {
          case 'pending':
            return 'info-value info-value-warning'
          case 'checked':
            if (this.getValueChanges().includes(key)) {
              return 'info-value info-value-error'
            }
            return 'info-value info-value-success'
          case 'not_required':
            return 'info-value info-value-success'
          default:
            return 'info-value'
        }
      },
      getProcessValue(field) {
        return checklistService.getProcessValue(this.processInfo, field)
      },
      getValueChanges() {
        return checklistService.getValueChanges(this.parameter, this.processInfo)
      },
      getValueClass(key) {
        // Si tiene cambios detectados, usar color de error
        if (this.getValueChanges().includes(key)) {
          return 'info-value info-value-error'
        }

        // Según el estado del parámetro
        switch (this.parameter.value) {
          case 'pending':
            return 'info-value info-value-warning'
          case 'checked':
            return 'info-value info-value-success'
          case 'not_required':
            return 'info-value info-value-success'
          default:
            return 'info-value'
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/styles/resources/colors.scss";

  .extra-info-header {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    font-weight: 600;
    color: #555;

    .extra-info-title {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .extra-info-content .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
    font-family: monospace;

    &:last-child {
      margin-bottom: 0;
    }

    .info-key {
      font-weight: 500;
      color: #333;
      min-width: 80px;
    }

    .info-value {
      color: #666;
      background: white;
      padding: 2px 6px;
      border-radius: 3px;
      border: 1px solid #ddd;
      font-size: 11px;
    }

    // Estado warning - pendiente de confirmar
    .info-value-warning {
      background-color: $color_app_bg_warning;
      border-color: $color_app_warning;
      color: #e65100;
    }

    // Estado success - confirmado
    .info-value-success {
      background-color: rgba(103, 150, 80, 0.15);
      border-color: $color_app_success;
      color: #2e7d32;
    }

    // Estado error - tiene cambios
    .info-value-error {
      background-color: $color_app_bg_error;
      border-color: $color_app_error;
      color: #c62828;
    }

    .info-value-changed {
      border-color: $color_app_error;
    }

    .info-value-current {
      border-color: $color_app_error;
      background-color: #fff8f8;
    }
  }
</style>